import React from 'react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { Button, Modal, Table } from 'react-bootstrap';
import { FailureIcon, ShieldIcon, SuccessIcon } from '../../Global/Icons';
import { LinkBadge } from '../../Global/Transitions';
import { Retest, RetestFinding } from '../../../Models/Retest';
import RatingProps from '../../../Utilities/RatingProps';
import { retestStatusStyle } from '../../../Utilities/RetestStatusProps';
import { quoteIconTint } from '../../../Utilities/QuoteProps';

interface Props {
	retest: Retest;
	projectId: string;
	canopyId?: number;
	onClose: () => void;
}

const rating = (severity?: number) =>
	RatingProps[severity as keyof typeof RatingProps] ?? RatingProps[0];

// Only when it actually moved. Printing "Low → Low" on every unresolved finding buries the ones
// that did move, which is the only thing this column is here to show.
const severityMovement = (finding: RetestFinding): string | null =>
	finding.severityAfter != null && finding.severityAfter !== finding.severityAtRequest
		? `${rating(finding.severityAtRequest).name} → ${rating(finding.severityAfter).name}`
		: null;

export default function RetestFindings({ retest, projectId, canopyId, onClose }: Props) {
	// A green tick above the word "Cancelled" contradicts itself, so a cancelled retest gets the
	// failure variant. The tint comes from QuoteProps, this repo's existing status→icon-colour
	// map; 'Quote Requested' carries an empty tint there, which SuccessIcon resolves to its own
	// default — left as the map has it rather than invented here.
	const StatusIcon = retest.status === 'Cancelled' ? FailureIcon : SuccessIcon;

	return (
		// Centred header on this app's confirmation-modal idiom (SetupQuote, EditProject,
		// RepeatAssessment); the findings table below is pulled back to the start.
		<Modal.Body className='text-center my-3 pt-3 px-xl-5'>
			<StatusIcon className={quoteIconTint(retest.status)} />
			<h1 className='m-0 pb-2'>{retest.status}</h1>
			<p>Requested {format(new Date(retest.requestedDate), 'dd/MM/yyyy')}</p>
			<hr className='my-4' />

			<Table hover size='sm' className='text-start'>
				<thead>
					<tr>
						<th>Severity</th>
						<th>Finding</th>
						<th>Decision</th>
						<th>Outcome</th>
					</tr>
				</thead>
				<tbody>
					{retest.findings.length === 0 ? (
						<tr>
							<td colSpan={4} style={{ textAlign: 'center', padding: '20px' }}>
								No findings available.
							</td>
						</tr>
					) : (
						retest.findings.map((finding) => {
							const before = rating(finding.severityAtRequest);
							const movement = severityMovement(finding);
							const riskAccepted = finding.disposition === 'RiskAccepted';
							const outcome = finding.retestStatusAfter?.trim();
							// Findings link back into the report by Canopy's ids. Without a Canopy project
							// there is no such route, so the title is plain text rather than a 404.
							const link =
								canopyId != null
									? `/projects/${projectId}/${canopyId}/reports/${finding.phaseId}/finding/${finding.findingId}`
									: null;

							return (
								<tr key={finding.findingUuid}>
									<td>
										<ShieldIcon className={'me-2 ' + before.color} />
										{movement ?? before.name}
									</td>
									<td>{link ? <Link to={link}>{finding.title}</Link> : finding.title}</td>
									<td>
										{riskAccepted ? 'Risk Accepted' : 'Retest'}
										{riskAccepted && finding.reason && (
											<div className='text-muted small'>{finding.reason}</div>
										)}
									</td>
									<td>
										{riskAccepted ? (
											// The decision IS the outcome here. A blank cell would misread as
											// "still waiting" on a finding that was never going to be retested.
											'—'
										) : outcome ? (
											// Verbatim: Canopy's retest_status is a free-form string that can gain
											// values this codebase has never seen, and retestStatusStyle already
											// falls back for anything it does not recognise.
											<LinkBadge
												bg={retestStatusStyle(outcome).bg}
												text={retestStatusStyle(outcome).text}
												border={retestStatusStyle(outcome).border}
											>
												{outcome}
											</LinkBadge>
										) : (
											// Outcomes are only snapshotted when a retest completes, so an
											// in-flight retest legitimately has none. Blank would read as an error.
											'Awaiting retest'
										)}
									</td>
								</tr>
							);
						})
					)}
				</tbody>
			</Table>

			{/* backdrop='static' and keyboard={false} on FadeModal mean the backdrop and Escape do
			    not dismiss, and this modal has no Modal.Header to carry a close button — so this is
			    the only way out. */}
			<Button variant='outline-primary' className='mt-4' onClick={onClose}>Close</Button>
		</Modal.Body>
	);
}
